import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {componentEnterLeaveAnimation} from '@application/animations/component-enter-leave.animation';
import {PrintDocument} from '@application/command/print-document';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {PlanningOrder} from '@domain/planning/planning-order';
import {PlanningOrderDetails} from '@domain/planning/planning-order-details';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {YieldUnit} from '@domain/yield-unit.enum';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {Planning, PLANNING} from '@infrastructure/http/planning/planning';
import {HttpProductionOrdersCustomSettingsService} from '@infrastructure/http/production-order-custom-settings/http-production-orders-custom-settings.service';
import {PRODUCTION_ORDERS, ProductionOrders} from '@infrastructure/http/production-order/production-orders';
import {ProductionOrderLiteStatusChange} from '@infrastructure/signalr/production-order-lite/production-order-lite-status-change';
import {REALTIME_PRODUCTION_ORDER_LITE, RealtimeProductionOrderLite} from '@infrastructure/signalr/production-order-lite/realtime-production-order-lite';
import {ProductionOrderStatusChange} from '@infrastructure/signalr/production-order/production-order-status-change';
import {REALTIME_PRODUCTION_ORDER, RealtimeProductionOrder} from '@infrastructure/signalr/production-order/realtime-production-order';
import {ConfirmProductionOrderDeletionCommand} from '@presentation/pages/texfab/planning/confirm-production-order-deletion/confirm-production-order-deletion-command';
import {ConfirmProductionScheduleOnDesktopCommand} from '@presentation/pages/texfab/production-schedule/confirm/confirm-production-schedule-on-desktop-command';
import {ConfirmProductionScheduleOnDesktopResult} from '@presentation/pages/texfab/production-schedule/confirm/confirm-production-schedule-on-desktop-result';
import {AlertDialogResult, AssertionUtils, BaseComponent, DialogBuilderFactoryService, DialogType, Message, MessageType, ToastService, TranslateService} from '@vdw/angular-component-library';
import {isEqual, isNil, lowerCase, replace} from 'lodash-es';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, takeUntil} from 'rxjs/operators';
import {PlanningSidebarData} from '../../planning-sidebar-data.interface';
import {PlanningSidebarType} from '../planning-sidebar-type.enum';

@Component({
  selector: 'app-planning-order-detail-sidebar',
  templateUrl: './planning-order-detail-sidebar.component.html',
  styleUrls: ['./planning-order-detail-sidebar.component.scss'],
  animations: [componentEnterLeaveAnimation()]
})
export class PlanningOrderDetailSidebarComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public planningOrderId: number;
  @Output() public planningOrderDetailSidebarClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() public delete: EventEmitter<void> = new EventEmitter<void>();

  public planningOrder: PlanningOrder;
  public machine: PlanningMachine;
  public canShowProcessAndQueueActions = true;
  public processAndQueueButtonsIsEnabled = true;
  public processing = false;
  public processingAndQueuing = false;
  public queuing = false;
  public reconfirmWarningMessage: Message[];

  public readonly dateFormat = 'dddd MMM D, YYYY';

  private productionOrdersCustomSettings: ProductionOrderCustomSettings;
  private reconfirming = false;
  private reconfirmed = false;
  private currentSubscription: Subscription;

  private readonly processAndQueueTranslationKey = 'PLANNING.ACTIONS.PROCESS_AND_QUEUE';
  private readonly queueTranslationKey = 'PLANNING.ACTIONS.QUEUE';

  public constructor(
    @Inject(PRODUCTION_ORDERS) private readonly productionOrders: ProductionOrders,
    @Inject(PLANNING) private readonly planning: Planning,
    @Inject(REALTIME_PRODUCTION_ORDER) private readonly realtimeProductionOrder: RealtimeProductionOrder,
    @Inject(REALTIME_PRODUCTION_ORDER_LITE) private readonly realtimeProductionOrderLite: RealtimeProductionOrderLite,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly httpProductionOrderCustomSettings: HttpProductionOrdersCustomSettingsService,
    private readonly toastService: ToastService,
    private readonly confirmProductionOrderDeletionCommand: ConfirmProductionOrderDeletionCommand,
    private readonly confirmProductionScheduleOnDesktopCommand: ConfirmProductionScheduleOnDesktopCommand,
    private readonly printDocument: PrintDocument,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly navigationHelperService: NavigationHelperService<PlanningSidebarData>
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.reconfirmWarningMessage = [new Message(null, this.translate.instant('PLANNING.WARNINGS.RECONFIRM'), MessageType.WARNING, null)];
    this.realtimeProductionOrder
      .getProductionOrdersStatusChanges()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(({id, status}: ProductionOrderStatusChange) => {
        if (this.planningOrder?.isCarpetOrder && id === this.planningOrder?.productionOrder.id) {
          this.planningOrder.productionOrder.status = status;
          this.canShowProcessAndQueueActions = true;
        }
      });

    this.realtimeProductionOrderLite
      .getProductionOrdersLiteStatusChanges()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(({id, status}: ProductionOrderLiteStatusChange) => {
        if (this.planningOrder?.isWeavingOrder && id === this.planningOrder?.productionOrder.id) {
          this.planningOrder.productionOrder.status = status;
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('planningOrderId' in changes && !isEqual(changes.planningOrderId.previousValue, this.planningOrderId)) {
      this.getPlanningOrderDetails();
      this.reconfirmed = false;
    }
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<PlanningSidebarData>({
      sidebarType: PlanningSidebarType.PLANNING_ORDER,
      sidebarPlanningOrderId: this.planningOrderId
    });
  }

  public closePlanningOrderDetail(): void {
    this.planningOrderDetailSidebarClose.emit();
  }

  public printWeavingOrderForProductionOrder(): void {
    forkJoin([
      this.productionOrders.getWeavingOrderXMLForProductionOrder(Number(this.planningOrder.productionOrder.id)),
      this.productionOrders.getWeavingOrderXSLTForProductionOrder(Number(this.planningOrder.productionOrder.id))
    ]).subscribe((result: [string, string]) => {
      this.printDocument.execute(result);
    });
  }

  public canViewProductionOrder(): boolean {
    return !this.isLoadingPlanningOrder() && !isNil(this.planningOrder.productionOrder.id);
  }

  public canViewActionsForPlanningOrder(): boolean {
    return this.planningOrder.canBeRescheduled() || this.canViewProductionOrder() || this.planningOrder.productionOrder.needsToBeReconfirmed();
  }

  public canRemoveOrder(): boolean {
    const hasPermissionToRemove =
      (this.planningOrder.isCarpetOrder && this.currentSubscription?.hasPermission(Permission.TEXFAB_CARPET_QUEUE)) ||
      (this.planningOrder.isWeavingOrder && this.currentSubscription?.hasPermission(Permission.TEXFAB_WEAVE_QUEUE)) ||
      (this.planningOrder.isTuftingOrder && this.currentSubscription?.hasPermission(Permission.TEXFAB_TUFT_QUEUE));

    return hasPermissionToRemove && this.planningOrder.canBeRescheduled();
  }

  public viewProductionOrder(): void {
    if (this.planningOrder.isCarpetOrder) {
      let path = RouteUtils.paths.texFab.productionOrder.editProductionOrder.absolutePath;
      this.router.navigate([replace(path, ':id', this.planningOrder.productionOrder.id.toString())]);
    } else if (this.planningOrder.isWeavingOrder) {
      this.router.navigate([replace(RouteUtils.paths.texFab.weaving.editProductionOrder.absolutePath, '/:id', ''), this.planningOrder.productionOrder.id]);
    } else if (this.planningOrder.isTuftingOrder) {
      this.router.navigate([replace(RouteUtils.paths.texFab.tufting.editProductionOrder.absolutePath, '/:id', ''), this.planningOrder.productionOrder.id]);
    }
  }

  public deletePlanningOrder(): void {
    const title = this.translate.instant('PLANNING.REMOVE_PRODUCTION_ORDER');
    const message = this.translate.instant('PLANNING.CONFIRM_PRODUCTION_ORDER_REMOVAL', {productionOrderName: this.planningOrder.productionOrder.name});

    this.confirmProductionOrderDeletionCommand
      .execute({title, message})
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        let deleteOrderCommand: Observable<void>;
        if (this.planningOrder.isCarpetOrder) {
          deleteOrderCommand = this.planning.deletePlanningOrder(this.planningOrder.productionOrder.id);
        } else if (this.planningOrder.isWeavingOrder) {
          deleteOrderCommand = this.planning.deletePlanningOrderLite(this.planningOrder.productionOrder.id);
        } else if (this.planningOrder.isTuftingOrder) {
          deleteOrderCommand = this.planning.deletePlanningOrderLite(this.planningOrder.productionOrder.id);
        }
        deleteOrderCommand?.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
          this.delete.emit();
        });
      });
  }

  public getValueForYield(): string {
    let result = '';

    if (!isNil(this.planningOrder.productionOrder.yield.planned)) {
      const yieldUnit = YieldUnit[YieldUnit.getYieldUnitForMachineType(this.machine.machineType)];
      const yieldUnitTranslation = !isNil(yieldUnit) ? this.translate.instant(`GENERAL.UNIT.${yieldUnit}`) : '';
      const yieldUnitSpacing = yieldUnit === YieldUnit[YieldUnit.METER] ? '' : ' ';
      result = `${this.planningOrder.productionOrder.yield.current}/${this.planningOrder.productionOrder.yield.planned}${yieldUnitSpacing}${yieldUnitTranslation}`;
    }

    return result;
  }

  public processProductionOrder(): void {
    if (!this.processingAndQueuing) {
      this.processing = true;
      this.planning
        .processProductionOrder(this.planningOrder.productionOrder.id)
        .pipe(
          takeUntil(this.unSubscribeOnViewDestroy),
          finalize(() => (this.processing = false))
        )
        .subscribe(() => {
          this.showInfoToast(this.translate.instant('PLANNING.PROCESSING', {name: this.planningOrder.productionOrder.name}));
          this.canShowProcessAndQueueActions = false;
        });
    }
  }

  public queueProductionOrder(): void {
    this.confirmAction(this.queueTranslationKey, () => {
      this.queuing = true;
      this.planning
        .queueProductionOrder(this.planningOrder.productionOrder.id)
        .pipe(
          takeUntil(this.unSubscribeOnViewDestroy),
          finalize(() => (this.queuing = false))
        )
        .subscribe(() => {
          this.showInfoToast(this.translate.instant('PLANNING.QUEUING', {name: this.planningOrder.productionOrder.name}));
          this.canShowProcessAndQueueActions = false;
        });
    });
  }

  public processAndQueueProductionOrder(): void {
    if (!this.processing) {
      this.confirmAction(this.processAndQueueTranslationKey, () => {
        this.processingAndQueuing = true;
        this.planning
          .processAndQueueProductionOrder(this.planningOrder.productionOrder.id)
          .pipe(
            takeUntil(this.unSubscribeOnViewDestroy),
            finalize(() => (this.processingAndQueuing = false))
          )
          .subscribe(() => {
            this.showInfoToast(this.translate.instant('PLANNING.PROCESSING_AND_QUEUING', {name: this.planningOrder.productionOrder.name}));
            this.canShowProcessAndQueueActions = false;
          });
      });
    }
  }

  public reconfirmProductionOrder(): void {
    if (!this.reconfirming) {
      this.reconfirming = true;

      if (AssertionUtils.isNullOrUndefined(this.productionOrdersCustomSettings)) {
        this.httpProductionOrderCustomSettings
          .get()
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe((productionOrderCustomSettings: ProductionOrderCustomSettings) => {
            this.productionOrdersCustomSettings = productionOrderCustomSettings;
            this.getAndReconfirmProductionOrder();
          });
      } else {
        this.getAndReconfirmProductionOrder();
      }
    }
  }

  public canReconfirmProductionOrder(): boolean {
    return this.planningOrder.productionOrder.needsToBeReconfirmed() && !this.reconfirmed;
  }

  public isLoadingPlanningOrder(): boolean {
    return isNil(this.planningOrder);
  }

  private getPlanningOrderDetails(): void {
    this.planningOrder = null;

    this.planning
      .getPlanningOrderDetails(this.planningOrderId)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(({machine, planningOrder}: PlanningOrderDetails) => {
        this.planningOrder = planningOrder;
        this.machine = machine;
        this.getReadyToQueue();
      });
  }

  public getReadyToQueue(): void {
    if (!this.planningOrder.isCarpetOrder) {
      this.processAndQueueButtonsIsEnabled = false;
      return;
    }
    this.productionOrders
      .getReadyToQueue(this.planningOrder.productionOrder.id)
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        catchError(() => {
          return of(false);
        })
      )
      .subscribe((isReadyToQueue: boolean) => {
        this.processAndQueueButtonsIsEnabled = isReadyToQueue;
      });
  }

  private showInfoToast(message: string): void {
    this.toastService.showInfo({
      tapToDismiss: true,
      timeOut: 5000,
      message
    });
  }

  private confirmAction(action: string, onConfirm: () => void): void {
    const actionTranslation = this.translate.instant(action);

    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: actionTranslation,
        messageText: this.translate.instant('PLANNING.CONFIRM_ACTION', {action: lowerCase(actionTranslation), name: this.planningOrder.productionOrder.name}),
        type: DialogType.CONFIRM
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((result: AlertDialogResult) => {
        if (isEqual(result, AlertDialogResult.CONFIRM)) {
          onConfirm();
        }
      });
  }

  private getAndReconfirmProductionOrder(): void {
    this.confirmProductionScheduleOnDesktopCommand
      .getAndConfirmProductionSchedule(this.planningOrder.productionOrder.id, this.productionOrdersCustomSettings)
      .pipe(
        finalize(() => (this.reconfirming = false)),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((confirmProductionScheduleOnDesktopResult: ConfirmProductionScheduleOnDesktopResult) => {
        if (isEqual(confirmProductionScheduleOnDesktopResult, ConfirmProductionScheduleOnDesktopResult.CONFIRMED)) {
          this.reconfirmed = true;
        }
      });
  }
}
